import { isEnvCdnUrl, getEnvCdnBaseUrl } from './cdnUtils';

/**
 * TEMPORARY URL UTILITIES
 * 
 * This file contains URL construction logic that should be REMOVED when the backend
 * provides these URLs directly. This is a temporary solution for:
 * 1. Video streaming URLs (CMAF/HLS)
 * 2. Thumbnail URLs (auto-generated thumbnails)
 * 
 * TODO: Remove this entire file when backend provides:
 * - Direct streaming URLs for videos
 * - Direct thumbnail URLs for all media
 */

// TEMPORARY: Import only what we need to avoid circular dependencies
// We'll duplicate these small utility functions here to avoid importing from mediaUtils

/**
 * TEMPORARY: Check if a URL is absolute (duplicated from mediaUtils to avoid circular dependency)
 */
function isAbsoluteUrl(url: string): boolean {
  if (!url) return false;
  return url.startsWith('http://') || url.startsWith('https://');
}

/**
 * TEMPORARY: Construct media URL (duplicated from mediaUtils to avoid circular dependency)
 */
function constructMediaUrl(url: string): string {
  if (!url) return '';
  
  // If it's already a blob URL or data URL, return as is
  if (url.startsWith('blob:') || url.startsWith('data:')) {
    return url;
  }
  
  // If it's already an absolute URL, return as is
  if (url.startsWith('http:') || url.startsWith('https:')) {
    return url;
  }
  
  // Otherwise, construct a URL relative to the current origin
  return `${window.location.origin}${url.startsWith('/') ? '' : '/'}${url}`;
}

/**
 * TEMPORARY: Properly encode video URLs with special characters
 * 
 * This function handles AWS S3's non-standard URL encoding behavior where:
 * - S3 object keys use '+' for spaces  
 * - Standard URL encoding uses '%20' for spaces
 * - Actual '+' characters become '%2B' when encoded
 * 
 * The function prevents double-encoding and ensures S3/CDN compatibility.
 * 
 * @param videoUrl - The video URL to encode
 * @returns string - The properly encoded video URL
 */
export function encodeVideoUrl(videoUrl: string): string {
  if (!videoUrl || !isAbsoluteUrl(videoUrl)) {
    return videoUrl;
  }
  
  try {
    // Parse the URL to separate components
    const url = new URL(videoUrl);
    
    const isKnownCdn = isEnvCdnUrl(url.toString());
    const isS3Host = url.hostname.includes('.amazonaws.com');
    const requiresS3Encoding = isKnownCdn || isS3Host;
    
    // Split the pathname into segments and handle each one
    const pathSegments = url.pathname.split('/');
    const encodedSegments = pathSegments.map((segment) => {
      // Don't encode empty segments (from leading/trailing slashes)
      if (segment === '') return segment;
      
      // Detect different encoding states to prevent double-encoding
      const hasPercentEncoding = segment.includes('%');
      const hasPlusEncoding = segment.includes('+');
      
      if (hasPercentEncoding) {
        // Already URL encoded - check if it's double-encoded
        if (segment.includes('%2B')) {
          // This is double-encoded! Decode once and re-encode properly
          console.warn(`[TEMP-URL] Double-encoded segment detected: ${segment}`);
          const decoded = decodeURIComponent(segment);
          return requiresS3Encoding
            ? encodeURIComponent(decoded).replace(/%20/g, '+')
            : encodeURIComponent(decoded);
        }
        // Already properly encoded, leave as-is
        return segment;
      } else if (hasPlusEncoding && requiresS3Encoding) {
        // For filenames with literal + signs, we need to encode them properly
        // The + signs in this case are part of the actual filename, not space encoding
        return encodeURIComponent(segment).replace(/%20/g, '+');
      } else {
        // Not encoded yet - apply appropriate encoding
        if (requiresS3Encoding) {
          // For S3/CDN URLs, encode spaces as '+' to match S3 object key format
          return encodeURIComponent(segment).replace(/%20/g, '+');
        } else {
          // For other URLs, use standard encoding
          return encodeURIComponent(segment);
        }
      }
    });
    
    // Reconstruct the URL with properly encoded path
    url.pathname = encodedSegments.join('/');
    
    return url.toString();
  } catch (error) {
    console.warn(`[TEMP-URL] Failed to encode video URL: ${videoUrl}`, error);
    return videoUrl;
  }
}

/**
 * TEMPORARY: S3-compatible URL encoding utility
 * 
 * S3 object keys use '+' for spaces, while standard URL encoding uses '%20'.
 * This function provides consistent S3-compatible encoding.
 * 
 * @param segment - The URL segment to encode
 * @param forS3 - Whether to use S3-compatible encoding (+ for spaces)
 * @returns string - The properly encoded segment
 */
export function encodeUrlSegmentForS3(segment: string, forS3: boolean = true): string {
  if (!segment) return segment;
  
  // Detect different encoding states to prevent double-encoding
  const hasPercentEncoding = segment.includes('%');
  const hasPlusEncoding = segment.includes('+');
  
  if (hasPercentEncoding) {
    // Already URL encoded - check if it's double-encoded
    if (segment.includes('%2B')) {
      // This is double-encoded! Decode once and return the result
      // Don't re-encode because decoding %2B gives us + which is what we want for S3
      console.warn(`[TEMP-URL] Double-encoded segment detected: ${segment}`);
      const decoded = decodeURIComponent(segment);
      return decoded; // Keep + signs as-is for S3 compatibility
    }
    // Already properly encoded, convert %20 to + for S3 if needed
    return forS3 ? segment.replace(/%20/g, '+') : segment;
  } else if (hasPlusEncoding && forS3) {
    // For filenames with literal + signs, we need to encode them properly
    // The + signs in this case are part of the actual filename, not space encoding
    return encodeURIComponent(segment).replace(/%20/g, '+');
  } else {
    // Not encoded yet - apply appropriate encoding
    if (forS3) {
      // For S3/CDN URLs, encode spaces as '+' to match S3 object key format
      return encodeURIComponent(segment).replace(/%20/g, '+');
    } else {
      // For other URLs, use standard encoding
      return encodeURIComponent(segment);
    }
  }
}

/**
 * TEMPORARY: Test function to verify URL encoding fixes
 * Remove this after confirming the fix works in production
 */
export function testUrlEncodingFix(): void {
  console.log('=== Testing URL Encoding Fix ===');
  
  // Test the exact problematic case from the issue
  const problematicUrl = `${getEnvCdnBaseUrl()}/org_7/2512/streaming/Good onya Malcolm.m3u8`;
  const result = encodeVideoUrl(problematicUrl);
  
  console.log('Input:', problematicUrl);
  console.log('Output:', result);
  console.log(`Expected: ${getEnvCdnBaseUrl()}/org_7/2512/streaming/Good+onya+Malcolm.m3u8`);
  console.log('✓ Correct format:', result.includes('Good+onya+Malcolm'));
  console.log('✓ No double encoding:', !result.includes('%2B'));
  
  // Test already double-encoded URL (the problematic case)
  const doubleEncodedUrl = `${getEnvCdnBaseUrl()}/org_7/2512/streaming/Good%2Bonya%2BMalcolm.m3u8`;
  const fixedResult = encodeVideoUrl(doubleEncodedUrl);
  
  console.log('\n=== Double-Encoded Fix Test ===');
  console.log('Input (problematic):', doubleEncodedUrl);
  console.log('Output (fixed):', fixedResult);
  console.log('✓ Fixed double encoding:', fixedResult.includes('Good+onya+Malcolm'));
  console.log('✓ No %2B present:', !fixedResult.includes('%2B'));
  
  console.log('=== Test Complete ===');
}

// =============================================================================
// VIDEO STREAMING URL CONSTRUCTION (TEMPORARY)
// =============================================================================

export interface VideoStreamingUrlResult {
  url: string;
  mode: 'cmaf' | 'streaming' | 'fallback';
  tested: boolean;
}

/**
 * TEMPORARY: Constructs potential video streaming URLs with fallback logic
 * 
 * This function tries to construct CMAF/HLS streaming URLs from the original video URL.
 * It checks multiple possible paths where the streaming manifest might be located.
 * 
 * @param originalUrl - The original video URL
 * @returns Promise<VideoStreamingUrlResult> - Result with URL, mode, and test status
 */
export async function getVideoStreamingUrl(originalUrl: string): Promise<VideoStreamingUrlResult> {
  if (!originalUrl || !isAbsoluteUrl(originalUrl)) {
    return { url: originalUrl, mode: 'fallback', tested: false };
  }

  // Skip streaming URL construction for local files and external services
  const isLocalFile = originalUrl.startsWith('blob:') || 
                     originalUrl.startsWith('data:') ||
                     originalUrl.includes('localhost') ||
                     originalUrl.includes('127.0.0.1') ||
                     originalUrl.includes(':3000') ||
                     originalUrl.includes('local.');

  const isExternalService = originalUrl.includes('youtube.com') ||
                           originalUrl.includes('youtu.be') ||
                           originalUrl.includes('vimeo.com');

  if (isLocalFile || isExternalService) {
    return { url: originalUrl, mode: 'fallback', tested: false };
  }

  // Extract filename without extension and base path
  const urlMatch = originalUrl.match(/^(https?:\/\/[^\/]+)(\/.*\/)([^\/]+)\.(mp4|mov|avi|webm|mkv|mts|m2ts)$/i);
  
  if (!urlMatch) {
    return { url: originalUrl, mode: 'fallback', tested: false };
  }

  const [, baseUrl, path, filename] = urlMatch;

  // Define potential streaming paths in order of preference
  // Use the improved S3-compatible encoding function
  const encodedFilename = encodeUrlSegmentForS3(filename, true);
  
  const streamingPaths = [
    `${baseUrl}${path}formatted_outputs/CMAF/${encodedFilename}.m3u8`,
    `${baseUrl}${path}streaming/${encodedFilename}.m3u8`
  ];

  // Test each streaming path
  // Note: Using GET instead of HEAD because some CDN configurations (CloudFront) 
  // block HEAD requests but allow GET requests for m3u8 files
  for (let i = 0; i < streamingPaths.length; i++) {
    const streamingUrl = streamingPaths[i];
    const mode = i === 0 ? 'cmaf' : 'streaming';
    
    
    try {
      const response = await fetch(streamingUrl, { 
        method: 'GET', 
        credentials: 'include', 
        mode: 'cors' 
      });
      
      if (response.ok) {
        console.log(`[TEMP-URL] Found streaming manifest: ${streamingUrl}`);
        return { url: streamingUrl, mode: mode as 'cmaf' | 'streaming', tested: true };
      }
    } catch (error) {
      console.log(`[TEMP-URL] Streaming URL not available: ${streamingUrl}`);
    }
  }

  // If no streaming URL found, return encoded original URL as fallback
  const encodedOriginalUrl = encodeVideoUrl(originalUrl);
  console.log(`[TEMP-URL] No streaming URL found, using fallback: ${encodedOriginalUrl}`);
  return { url: encodedOriginalUrl, mode: 'fallback', tested: true };
}

/**
 * TEMPORARY: Synchronous version that constructs streaming URLs without testing
 * 
 * This is used when you need a streaming URL immediately but can test it later.
 * It returns the first potential streaming URL without checking if it exists.
 * 
 * @param originalUrl - The original video URL
 * @returns string - The constructed streaming URL or original URL as fallback
 */
export function constructVideoStreamingUrl(originalUrl: string): string {
  if (!originalUrl || !isAbsoluteUrl(originalUrl)) {
    return originalUrl;
  }

  // Skip streaming URL construction for local files and external services
  const isLocalFile = originalUrl.startsWith('blob:') || 
                     originalUrl.startsWith('data:') ||
                     originalUrl.includes('localhost') ||
                     originalUrl.includes('127.0.0.1') ||
                     originalUrl.includes(':3000') ||
                     originalUrl.includes('local.');

  const isExternalService = originalUrl.includes('youtube.com') ||
                           originalUrl.includes('youtu.be') ||
                           originalUrl.includes('vimeo.com');

  if (isLocalFile || isExternalService) {
    return originalUrl;
  }

  // Extract filename without extension and base path
  const urlMatch = originalUrl.match(/^(https?:\/\/[^\/]+)(\/.*\/)([^\/]+)\.(mp4|mov|avi|webm|mkv|mts|m2ts)$/i);
  
  if (!urlMatch) {
    return originalUrl;
  }

  const [, baseUrl, path, filename] = urlMatch;

  // Use the improved S3-compatible encoding function
  const encodedFilename = encodeUrlSegmentForS3(filename, true);
  
  return `${baseUrl}${path}formatted_outputs/CMAF/${encodedFilename}.m3u8`;
}

// =============================================================================
// THUMBNAIL URL CONSTRUCTION (TEMPORARY)
// =============================================================================

export interface ThumbnailUrlResult {
  url: string;
  source: 'cdn' | 'server' | 'fallback';
  tested: boolean;
}

/**
 * TEMPORARY: Constructs a CDN thumbnail URL by replacing the filename with 'thumbnail.jpg'
 * 
 * This function tries to construct a thumbnail URL that should exist next to the original
 * media file on the CDN. This is temporary until the backend provides thumbnail URLs directly.
 * 
 * @param fullMediaUrl - The full media URL from the CDN
 * @returns string | undefined - The constructed thumbnail URL, or undefined if construction fails
 */
export function constructCdnThumbnailUrl(fullMediaUrl: string): string | undefined {
  if (!fullMediaUrl || !isAbsoluteUrl(fullMediaUrl)) {
    return undefined;
  }
  
  try {
    const url = new URL(fullMediaUrl);
    const pathSegments = url.pathname.split('/');
    
    if (pathSegments.length > 1) {
      // Idempotency guard: if the URL already points at a thumbnail file
      // (e.g. a server-provided `thumbnail.png` for spatial datasets), return it
      // verbatim. Re-deriving would clobber the real extension to `.jpg` and 404.
      const lastSegment = pathSegments[pathSegments.length - 1];
      if (/^thumbnail\.[a-z0-9]+$/i.test(lastSegment)) {
        return fullMediaUrl;
      }
      // Replace the last segment with 'thumbnail.jpg' (no encoding needed for simple filename)
      pathSegments[pathSegments.length - 1] = 'thumbnail.jpg';
      return `${url.protocol}//${url.host}${pathSegments.join('/')}`;
    }
  } catch (error) {
    console.log(`[TEMP-URL] Failed to construct CDN thumbnail URL: ${error}`);
  }
  
  return undefined;
}

/**
 * TEMPORARY: Gets a thumbnail URL with fallback logic
 * 
 * This function tries multiple sources for thumbnail URLs:
 * 1. CDN thumbnail.jpg (next to original file)
 * 2. Server-provided thumbnail URLs
 * 3. Fallback to original media URL (if appropriate)
 * 
 * @param item - The media item object
 * @returns Promise<ThumbnailUrlResult> - Result with URL, source, and test status
 */
export async function getThumbnailUrl(item: any): Promise<ThumbnailUrlResult> {
  if (!item) {
    return { url: '', source: 'fallback', tested: false };
  }

  // Normalize remote_details which can arrive as a JSON string in some API responses
  let remoteDetails = item.remote_details;
  if (typeof remoteDetails === "string") {
    try {
      remoteDetails = JSON.parse(remoteDetails);
    } catch {
      // ignore parse failures
    }
  }

  // Embeds often have `remote_details.url` that points to an HTML page (not a media file),
  // so constructing `.../thumbnail.jpg` from it produces broken URLs. Prefer server-provided
  // thumbnails. Note: client-created embed records have `media_type === null` (the backend
  // auto-derives it from an uploaded `file`, which embeds don't have), so we must also
  // consult `remote_details.embed_url` for embed detection.
  const mediaType = (item.media_type || item.type || "").toString().toLowerCase();
  const isEmbed =
    mediaType.startsWith("embed/") ||
    mediaType === "embed" ||
    Boolean(item.embed_url) ||
    Boolean(remoteDetails?.embed_url);

  if (isEmbed) {
    const serverThumbnailSources = [
      item.entity_thumbnail, // For RelatedEntityItem
      item.thumbnail, // For regular items
      item.primary_image?.thumbnail, // For items with primary_image
      remoteDetails?.thumbnail // For items with remote_details
    ];

    for (const thumbnailUrl of serverThumbnailSources) {
      if (thumbnailUrl && thumbnailUrl !== null) {
        console.log(`[TEMP-URL] (embed) Using server thumbnail: ${thumbnailUrl}`);
        return {
          url: constructMediaUrl(thumbnailUrl),
          source: "server",
          tested: false
        };
      }
    }

    // Embed with no server thumbnail: don't fall through to CDN-guess against a
    // provider HTML page URL — that produces broken `.../thumbnail.jpg` URLs.
    return { url: "", source: "fallback", tested: false };
  }

  // PRIORITY 1: Try CDN thumbnail first (thumbnail.jpg next to full media file)
  // Note: Using GET instead of HEAD because some CDN configurations (CloudFront)
  // block HEAD requests but allow GET requests.
  const fullMediaUrl = item.url || item.primary_image?.url || remoteDetails?.url;
  if (fullMediaUrl) {
    const cdnThumbnailUrl = constructCdnThumbnailUrl(fullMediaUrl);
    if (cdnThumbnailUrl) {
      try {
        const response = await fetch(cdnThumbnailUrl, { 
          method: 'GET', 
          credentials: 'include', 
          mode: 'cors' 
        });
        
        if (response.ok) {
          console.log(`[TEMP-URL] Found CDN thumbnail: ${cdnThumbnailUrl}`);
          return { url: cdnThumbnailUrl, source: 'cdn', tested: true };
        }
      } catch (error) {
        console.log(`[TEMP-URL] CDN thumbnail not available: ${cdnThumbnailUrl}`);
      }
    }
  }

  // PRIORITY 2: Fall back to server-provided thumbnail URLs
  const serverThumbnailSources = [
    item.entity_thumbnail, // For RelatedEntityItem
    item.thumbnail, // For regular items
    item.primary_image?.thumbnail, // For items with primary_image
    remoteDetails?.thumbnail // For items with remote_details
  ];

  for (const thumbnailUrl of serverThumbnailSources) {
    if (thumbnailUrl && thumbnailUrl !== null) {
      console.log(`[TEMP-URL] Using server thumbnail: ${thumbnailUrl}`);
      return { 
        url: constructMediaUrl(thumbnailUrl), 
        source: 'server', 
        tested: false 
      };
    }
  }

  // PRIORITY 3: No thumbnail found
  console.log(`[TEMP-URL] No thumbnail found for item:`, item.id);
  return { url: '', source: 'fallback', tested: true };
}

/**
 * TEMPORARY: Synchronous version that constructs thumbnail URLs without testing
 * 
 * This is used when you need a thumbnail URL immediately but can test it later.
 * It returns the first potential thumbnail URL without checking if it exists.
 * 
 * @param item - The media item object
 * @returns string | undefined - The constructed thumbnail URL or undefined if no sources
 */
export function constructThumbnailUrl(item: any): string | undefined {
  if (!item) return undefined;

  // Normalize remote_details which can arrive as a JSON string in some API responses
  let remoteDetails = item.remote_details;
  if (typeof remoteDetails === "string") {
    try {
      remoteDetails = JSON.parse(remoteDetails);
    } catch {
      // ignore parse failures
    }
  }

  // Embeds often have `remote_details.url` that points to an HTML page (not a media file),
  // so constructing `.../thumbnail.jpg` from it produces broken URLs. Prefer server-provided
  // thumbnails. Note: client-created embed records have `media_type === null` (the backend
  // auto-derives it from an uploaded `file`, which embeds don't have), so we must also
  // consult `remote_details.embed_url` for embed detection.
  const mediaType = (item.media_type || item.type || "").toString().toLowerCase();
  const isEmbed =
    mediaType.startsWith("embed/") ||
    mediaType === "embed" ||
    Boolean(item.embed_url) ||
    Boolean(remoteDetails?.embed_url);

  if (isEmbed) {
    const serverThumbnailSources = [
      item.entity_thumbnail, // For RelatedEntityItem
      item.thumbnail, // For regular items
      item.primary_image?.thumbnail, // For items with primary_image
      remoteDetails?.thumbnail // For items with remote_details
    ];

    for (const thumbnailUrl of serverThumbnailSources) {
      if (thumbnailUrl && thumbnailUrl !== null) {
        return constructMediaUrl(thumbnailUrl);
      }
    }

    // Embed with no server thumbnail: don't fall through to the CDN-guess
    // path below — it would produce broken `.../thumbnail.jpg` URLs against
    // provider HTML pages (e.g. https://www.youtube.com/thumbnail.jpg).
    return undefined;
  }

  // 1. CDN thumbnail from primary item URL (item.url or primary_image.url — both are CDN URLs)
  const primaryUrl = item.url || item.primary_image?.url;
  if (primaryUrl) {
    const cdnThumbnailUrl = constructCdnThumbnailUrl(primaryUrl);
    if (cdnThumbnailUrl) {
      return cdnThumbnailUrl;
    }
  }

  // 2. item.thumbnail is the authoritative server-set CDN thumbnail (v2 list API always populates
  //    this as the real CDN thumbnail URL). Check it before remote_details.url, which may be a
  //    direct S3 URL — constructing a thumbnail path from an S3 URL produces an inaccessible URL.
  if (item.thumbnail && isAbsoluteUrl(item.thumbnail)) {
    const cdnThumbnailUrl = constructCdnThumbnailUrl(item.thumbnail);
    if (cdnThumbnailUrl) {
      return cdnThumbnailUrl;
    }
  }

  // 3. Last resort: try to derive a thumbnail from remote_details.url (only reached when both
  //    item.url and item.thumbnail are absent — e.g. older v1 items without a thumbnail field)
  if (remoteDetails?.url) {
    const cdnThumbnailUrl = constructCdnThumbnailUrl(remoteDetails.url);
    if (cdnThumbnailUrl) {
      return cdnThumbnailUrl;
    }
  }

  // Fall back to server-provided thumbnail URLs
  const serverThumbnailSources = [
    item.entity_thumbnail, // For RelatedEntityItem
    item.thumbnail, // For regular items (only if not used above)
    item.primary_image?.thumbnail, // For items with primary_image
    remoteDetails?.thumbnail // For items with remote_details
  ];

  for (const thumbnailUrl of serverThumbnailSources) {
    if (thumbnailUrl && thumbnailUrl !== null) {
      return constructMediaUrl(thumbnailUrl);
    }
  }

  return undefined;
}

// =============================================================================
// HELPER FUNCTIONS
// =============================================================================

/**
 * TEMPORARY: Test if a URL is accessible
 * 
 * Note: Using GET instead of HEAD because some CDN configurations (CloudFront)
 * block HEAD requests but allow GET requests.
 * 
 * @param url - The URL to test
 * @returns Promise<boolean> - True if URL is accessible, false otherwise
 */
export async function testUrlExists(url: string): Promise<boolean> {
  try {
    const response = await fetch(url, { 
      method: 'GET', 
      credentials: 'include', 
      mode: 'cors' 
    });
    return response.ok;
  } catch (error) {
    return false;
  }
}

/**
 * TEMPORARY: Batch test multiple URLs and return the first that exists
 * 
 * @param urls - Array of URLs to test
 * @returns Promise<string | null> - First URL that exists, or null if none exist
 */
export async function findFirstExistingUrl(urls: string[]): Promise<string | null> {
  for (const url of urls) {
    if (await testUrlExists(url)) {
      return url;
    }
  }
  return null;
}